import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import HudLoader from '../components/HudLoader'
import {
  Zap,
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  BarChart3,
  Target,
  Settings,
} from 'lucide-react'
import { format } from 'date-fns'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

interface OverviewData {
  channels: number
  videos: { total: number; by_status: Record<string, number>; published: number }
  jobs: { total: number; by_status: Record<string, number> }
  topics: { total: number; by_status: Record<string, number> }
}

interface PipelineData {
  total_pipelines: number
  success_rate_pct: number
  avg_stage_duration_seconds: Record<string, number>
}

interface CostData {
  videos_produced: number
  estimated_openai_tokens: number
  estimated_tts_minutes: number
  estimated_cost_usd: { script_generation: number; text_to_speech: number; total: number }
}

function MetricCard({ title, value, icon: Icon, color, subtitle }: {
  title: string
  value: string | number
  icon: React.ComponentType<any>
  color: string
  subtitle?: string
}) {
  return (
    <div className="metric-card">
      <div className="metric-icon" style={{ backgroundColor: `${color}15`, color }}>
        <Icon size={24} />
      </div>
      <div className="metric-value">{value}</div>
      <div className="metric-title">{title}</div>
      {subtitle && <div className="metric-subtitle">{subtitle}</div>}
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-card">
      <h3>{title}</h3>
      <div className="chart-container">{children}</div>
    </div>
  )
}

export default function Metrics() {
  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [days, setDays] = useState(30)

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<{ id: string; title: string }[]>('/channels'),
  })

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['metrics', 'overview', selectedChannel, days],
    queryFn: () => selectedChannel ? api.get<OverviewData>(`/metrics/overview?channel_id=${selectedChannel}&days=${days}`) : null,
    enabled: !!selectedChannel,
  })

  const { data: pipeline, isLoading: pipelineLoading } = useQuery({
    queryKey: ['metrics', 'pipeline', selectedChannel, days],
    queryFn: () => selectedChannel ? api.get<PipelineData>(`/metrics/pipeline-performance?channel_id=${selectedChannel}&days=${days}`) : null,
    enabled: !!selectedChannel,
  })

  const { data: cost, isLoading: costLoading } = useQuery({
    queryKey: ['metrics', 'cost', selectedChannel, days],
    queryFn: () => selectedChannel ? api.get<CostData>(`/metrics/cost-estimate?channel_id=${selectedChannel}&days=${days}`) : null,
    enabled: !!selectedChannel,
  })

  if (!selectedChannel) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Metrics</h1>
          <p>Pipeline performance, costs, and operational metrics</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <BarChart3 size={48} />
            <h3>Select a channel</h3>
            <p>Choose a channel to view detailed metrics</p>
            <select
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
              className="select"
              style={{ maxWidth: 300, marginTop: 16 }}
            >
              <option value="">Select Channel</option>
              {channels?.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        </div>
      </div>
    )
  }

  const isLoading = overviewLoading || pipelineLoading || costLoading

  // Mock stage data for demo
  const stageData = pipeline?.avg_stage_duration_seconds
    ? Object.entries(pipeline.avg_stage_duration_seconds).map(([stage, seconds]) => ({ stage, seconds: Math.round(seconds) }))
    : [
        { stage: 'Research', seconds: 45 },
        { stage: 'Script', seconds: 120 },
        { stage: 'Voice', seconds: 60 },
        { stage: 'Visual', seconds: 30 },
        { stage: 'Assemble', seconds: 180 },
        { stage: 'Metadata', seconds: 25 },
        { stage: 'Compliance', seconds: 15 },
        { stage: 'Publish', seconds: 40 },
      ]

  const statusData = overview?.jobs?.by_status
    ? Object.entries(overview.jobs.by_status).map(([status, count]) => ({ status, count }))
    : [
        { status: 'Completed', count: 45 },
        { status: 'Running', count: 3 },
        { status: 'Failed', count: 2 },
        { status: 'Pending', count: 5 },
      ]

  const videoStatusData = overview?.videos?.by_status
    ? Object.entries(overview.videos.by_status).map(([status, count]) => ({ status, count }))
    : [
        { status: 'Draft', count: 5 },
        { status: 'Scripted', count: 3 },
        { status: 'Published', count: 12 },
        { status: 'Failed', count: 1 },
      ]

  const topicStatusData = overview?.topics?.by_status
    ? Object.entries(overview.topics.by_status).map(([status, count]) => ({ status, count }))
    : [
        { status: 'Discovered', count: 8 },
        { status: 'Researched', count: 5 },
        { status: 'Approved', count: 3 },
        { status: 'In Production', count: 2 },
      ]

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Metrics</h1>
          <p>Pipeline performance and cost analysis for {channels?.find((c: any) => c.id === selectedChannel)?.title}</p>
        </div>
        <div className="header-actions">
          <select value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)} className="select" style={{ minWidth: 250 }}>
            {channels?.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))} className="select" style={{ width: 140 }}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <HudLoader text="Loading metrics" />
      ) : (
        <>
          <div className="metrics-grid">
            <MetricCard title="Channels" value={overview?.channels || 0} icon={Zap} color="#3b82f6" subtitle={`${overview?.videos?.published || 0} published videos`} />
            <MetricCard title="Pipeline Success Rate" value={`${pipeline?.success_rate_pct || 0}%`} icon={CheckCircle} color="#10b981" subtitle={`${pipeline?.total_pipelines || 0} pipelines run`} />
            <MetricCard title="Avg Pipeline Duration" value={pipeline?.avg_stage_duration_seconds ? `${Math.round(Object.values(pipeline.avg_stage_duration_seconds).reduce((a, b) => a + b, 0) / 60)} min` : 'N/A'} icon={Clock} color="#f59e0b" subtitle="All stages combined" />
            <MetricCard title="Est. Monthly Cost" value={`$${cost?.estimated_cost_usd?.total.toFixed(2) || '0.00'}`} icon={DollarSign} color="#8b5cf6" subtitle={`${cost?.estimated_tts_minutes || 0} min TTS, ${cost?.estimated_openai_tokens || 0} tokens`} />
            <MetricCard title="Active Jobs" value={overview?.jobs?.by_status?.running || 0} icon={Zap} color="#ec4899" subtitle={`${overview?.jobs?.total || 0} total jobs`} />
            <MetricCard title="Videos Produced" value={cost?.videos_produced || 0} icon={Target} color="#6366f1" subtitle="This period" />
          </div>

          <div className="charts-grid">
            <ChartCard title="Pipeline Stage Duration (Average)">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stageData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 12 }} label={{ value: 'Seconds', position: 'insideTop' }} />
                  <YAxis type="category" dataKey="stage" width={120} stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: number) => [value, 'Seconds']} />
                  <Bar dataKey="seconds" fill="#3b82f6" radius={[4, 0, 0, 4]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Job Status Distribution">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {statusData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value, 'Jobs']} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Video Status Distribution">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={videoStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {videoStatusData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value, 'Videos']} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Topic Status Distribution">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={topicStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {topicStatusData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value, 'Topics']} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="grid grid-2 mt-4">
            <div className="card">
              <h2>Cost Breakdown</h2>
              <div className="cost-breakdown">
                <div className="cost-item">
                  <span>Script Generation (OpenAI)</span>
                  <span>${cost?.estimated_cost_usd?.script_generation.toFixed(4) || '0.0000'}</span>
                </div>
                <div className="cost-item">
                  <span>Text-to-Speech (OpenAI)</span>
                  <span>${cost?.estimated_cost_usd?.text_to_speech.toFixed(4) || '0.0000'}</span>
                </div>
                <div className="cost-item total">
                  <span>Total Estimated</span>
                  <span>${cost?.estimated_cost_usd?.total.toFixed(4) || '0.0000'}</span>
                </div>
                <div className="cost-details">
                  <p>~{cost?.estimated_openai_tokens?.toLocaleString() || 0} tokens • ~{cost?.estimated_tts_minutes || 0} min TTS</p>
                  <p className="muted">Based on gpt-4o-mini + gpt-4o-mini-tts pricing</p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2>Pipeline Health</h2>
              <div className="health-indicators">
                <div className="health-item">
                  <div className="health-icon success"><CheckCircle size={20} /></div>
                  <div>
                    <h4>Success Rate</h4>
                    <p>{pipeline?.success_rate_pct?.toFixed(1) || 0}% of pipelines complete successfully</p>
                  </div>
                </div>
                <div className="health-item">
                  <div className="health-icon warning"><AlertCircle size={20} /></div>
                  <div>
                    <h4>Failure Analysis</h4>
                    <p>Most failures in: {Object.entries(pipeline?.avg_stage_duration_seconds || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'} stage</p>
                  </div>
                </div>
                <div className="health-item">
                  <div className="health-icon info"><Settings size={20} /></div>
                  <div>
                    <h4>Optimization</h4>
                    <p>Consider parallelizing: {Object.entries(pipeline?.avg_stage_duration_seconds || {}).filter(([, v]) => v > 100).map(([k]) => k).join(', ') || 'None'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-4">
            <h2>Stage Performance Details</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Avg Duration</th>
                    <th>% of Total</th>
                    <th>Bottleneck Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {stageData.map((s) => {
                    const total = stageData.reduce((sum, d) => sum + d.seconds, 0)
                    const pct = ((s.seconds / total) * 100).toFixed(1)
                    return (
                      <tr key={s.stage}>
                        <td>{s.stage}</td>
                        <td>{s.seconds}s</td>
                        <td>{pct}%</td>
                        <td>
                          <span className={`badge ${s.seconds > 100 ? 'badge-danger' : s.seconds > 50 ? 'badge-warning' : 'badge-success'}`}>
                            {s.seconds > 100 ? 'High' : s.seconds > 50 ? 'Medium' : 'Low'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
